import { FiHash } from 'react-icons/fi';

export const understandingNumbersData = {
  courseId: 'understanding-numbers',
  courseType: 'listening-course',
  title: 'Understanding Numbers & Spelling',
  icon: FiHash,
  overview: {
    impact: 'Accurately capture numbers, dates, prices, and spelled words — the most frequent answer types in Sections 1 and 2',
    focus: 'Distinguishing teen/ty numbers, date and time formats, phone numbers, prices, addresses, postcodes, and letter-by-letter spelling',
    uses: 'IELTS Listening Sections 1–2 (form completion, note completion, table completion)',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'Numbers: Teens vs. Tens and Beyond',
      content: `<p>One of the most common traps in IELTS Listening is confusing <strong>teen numbers</strong> (13, 14, 15, 16, 17, 18, 19) with <strong>tens</strong> (30, 40, 50, 60, 70, 80, 90). The difference lies in <em>stress</em>: teen numbers stress the second syllable (<em>thir-TEEN</em>), while tens stress the first syllable (<em>THIR-ty</em>). Speakers often self-correct: "That's thirty — one-three — thirteen."</p>
<p>You'll also encounter <strong>decimals</strong> (e.g., "three point five"), <strong>large numbers</strong> spoken in groups (e.g., "twelve thousand, four hundred and fifty"), and <strong>fractions</strong> ("a quarter", "two thirds"). Always note the context — a price is unlikely to be "fifteen hundred" when the conversation is about a bus ticket.</p>`,
      points: [
        'Teen numbers (13–19) have stress on the SECOND syllable; tens (30–90) stress the FIRST syllable.',
        'Speakers frequently self-correct or repeat numbers — stay alert for the final version.',
        'Decimals are read digit by digit after the point: 4.75 = "four point seven five."',
        'Large numbers use grouping: 12,450 = "twelve thousand, four hundred and fifty."',
        'Zero can be said as "oh," "zero," or "nought" depending on context (phone vs. temperature).',
      ],
      warning: 'If you hear a speaker correct themselves ("Sorry, I meant…"), always write the corrected number, not the first one.',
      examples: [
        { label: 'Teen vs. Ten', text: '"The class has thirty — sorry, thirteen — students." → Answer: 13' },
        { label: 'Large number', text: '"The population is two hundred and fifty thousand." → Answer: 250,000' },
      ],
    },
    {
      title: 'Dates and Times',
      content: `<p>IELTS uses both <strong>British</strong> and <strong>international date formats</strong>. You may hear "the third of May," "May third," or simply "3/5" — and the answer sheet accepts several formats. For times, listen for <strong>12-hour clock</strong> phrases like "half past two," "quarter to six," and "ten fifteen," as well as <strong>24-hour clock</strong> in formal contexts ("fourteen hundred hours").</p>
<p>Key vocabulary: <em>a.m. / p.m.</em>, <em>noon / midday / midnight</em>, <em>quarter past / half past / quarter to</em>. Days and months are sometimes spelled out: "That's on Wednesday the fifteenth of September."</p>`,
      points: [
        'Acceptable date formats: 3 May, May 3, 3rd May, 03/05 — but be consistent.',
        '"Quarter past 2" = 2:15; "half past 2" = 2:30; "quarter to 3" = 2:45.',
        'Listen for a.m./p.m. — miswriting the period can cost a mark.',
        'Months can appear as words or numbers: "the seventh month" = July.',
        'Ordinal numbers (1st, 2nd, 3rd, 21st, 22nd) are common with dates.',
      ],
      examples: [
        { label: 'Date', text: '"Your appointment is on the twenty-second of March." → Answer: 22 March / March 22' },
        { label: 'Time', text: '"We close at quarter to nine in the evening." → Answer: 8:45 p.m.' },
      ],
    },
    {
      title: 'Telephone Numbers, Postcodes, and Addresses',
      content: `<p>Phone numbers are read <strong>digit by digit</strong>. A double digit is often signaled: "double five" = 55, "triple oh" = 000. Pauses group the digits (e.g., "oh-seven-seven … nine-four-three … two-six-one-eight"). UK postcodes follow a letter-number pattern like "SW1A 2AA," and speakers will spell them out.</p>
<p>Addresses combine house numbers, street names (often spelled), and postcodes. Listen for signal words: "It's number 47," "on Pemberton Road — that's P-E-M-B-E-R-T-O-N," "postcode CB4 3LT."</p>`,
      points: [
        'Phone numbers are given one digit at a time; "double" or "triple" signals repeated digits.',
        'Zero in phone numbers is usually said as "oh" (British) or "zero."',
        'UK postcodes mix letters and numbers: the speaker will spell letter portions.',
        'House/flat numbers come before the street name: "42 King Street."',
        'If unsure, write what you hear and move on — you can review during transfer time.',
      ],
      table: {
        headers: ['Spoken Form', 'Written Form'],
        rows: [
          ['oh-seven-seven-double-four-nine-three-six-one', '077 449 361'],
          ['double-oh-four-four', '0044'],
          ['twenty-seven Maple Avenue', '27 Maple Avenue'],
          ['postcode: N-W-one, four-B-T', 'NW1 4BT'],
        ],
      },
    },
    {
      title: 'Spelling and Names',
      content: `<p>Speakers spell out <strong>proper nouns</strong> — surnames, street names, place names — letter by letter. You must know the English names for every letter, especially easily confused pairs: <strong>A/R</strong>, <strong>B/P</strong>, <strong>G/J</strong>, <strong>M/N</strong>, <strong>I/Y</strong>. The vowel sound in "M" (/ɛm/) and "N" (/ɛn/) is nearly identical, so context matters.</p>
<p>Speakers signal spelling with cues like "That's spelled…," "Let me spell that for you," or simply start spelling. Some correct mid-spelling: "That's J-O-H-N-S — no, sorry — J-O-N-E-S."</p>`,
      points: [
        'Memorize the English alphabet pronunciation — especially B/P, G/J, M/N, I/Y, S/F.',
        '"Double" before a letter means two of that letter: "double L" = LL.',
        'Always write the corrected spelling if the speaker self-corrects.',
        'Capital letters are assumed for proper nouns; you don\'t need to worry about case.',
        'Practice writing while listening — speed matters as the audio keeps going.',
      ],
      warning: 'Names from many cultures appear in IELTS. Don\'t assume a spelling — always listen for the letters.',
      examples: [
        { label: 'Surname', text: '"My surname is Macquarie — that\'s M-A-C-Q-U-A-R-I-E." → Answer: Macquarie' },
        { label: 'Correction', text: '"That\'s O-double-L-I — oh sorry, O-L-I-V-E-R." → Answer: Oliver' },
      ],
    },
  ],
  exercises: [
    {
      title: 'A1 warm-up (play audio at 0.8× if available)',
      type: 'select',
      instructions: 'Start with easy numbers. If your player has speed control, use 0.8×.',
      questions: [
        {
          text: 'You hear: "two". Which number is it?',
          options: ['2', '12', '20'],
          answer: '2',
          explanation: 'The short word "two" is 2, not twelve or twenty.',
        },
        {
          text: 'You hear: "ten". Which number is it?',
          options: ['2', '10', '13'],
          answer: '10',
          explanation: '"Ten" is 10.',
        },
        {
          text: 'You hear: "fifteen". Which number is it?',
          options: ['15', '50', '5'],
          answer: '15',
          explanation: 'fif-TEEN (stress on the end) is 15, not 50.',
        },
      ],
    },
    {
      title: 'Choose the Correct Number',
      type: 'select',
      instructions: 'A speaker says each number aloud. Select the correct written form.',
      questions: [
        {
          text: 'The speaker says: "The lecture hall seats THIR-teen people." What number should you write?',
          options: ['13', '30', '31'],
          answer: '13',
          explanation: 'Stress on the second syllable (-TEEN) indicates thirteen, not thirty.',
        },
        {
          text: 'The speaker says: "That\'ll be fifteen pounds fifty." What is the price?',
          options: ['£15.50', '£50.15', '£50.50'],
          answer: '£15.50',
          explanation: '"Fifteen pounds fifty" means £15 and 50 pence = £15.50.',
        },
        {
          text: 'The speaker says: "The building was constructed in nineteen oh six." What year?',
          options: ['1960', '1906', '1916'],
          answer: '1906',
          explanation: '"Nineteen oh six" = 1906. "Oh" represents zero in the tens place.',
        },
        {
          text: 'The speaker says: "We need eighty — sorry, eighteen — copies." How many copies?',
          options: ['80', '18', '8'],
          answer: '18',
          explanation: 'The speaker corrected from eighty to eighteen. Always use the correction.',
        },
        {
          text: 'The speaker says: "The distance is nought point seven five kilometres." What is the distance?',
          options: ['7.5 km', '0.75 km', '75 km'],
          answer: '0.75 km',
          explanation: '"Nought point seven five" = 0.75. "Nought" means zero.',
        },
      ],
    },
    {
      title: 'Write the Correct Date or Time',
      type: 'fill-in',
      instructions: 'Listen to each statement and write the date or time in the correct format.',
      questions: [
        {
          text: 'The speaker says: "Your interview is on the fourteenth of February." Write the date: ___',
          answer: '14 February',
          explanation: '"The fourteenth of February" = 14 February. "February 14" is also accepted.',
        },
        {
          text: 'The speaker says: "The museum opens at quarter past ten in the morning." Write the time: ___',
          answer: '10:15 a.m.',
          explanation: '"Quarter past ten" = 10:15. "In the morning" confirms a.m.',
        },
        {
          text: 'The speaker says: "The deadline is the thirty-first of August, twenty twenty-six." Write the full date: ___',
          answer: '31 August 2026',
          explanation: 'Write day + month + year. "31/08/2026" is also acceptable.',
        },
        {
          text: 'The speaker says: "The train departs at half past seven in the evening." Write the time: ___',
          answer: '7:30 p.m.',
          explanation: '"Half past seven" = 7:30. "In the evening" = p.m.',
        },
      ],
    },
    {
      title: 'Identify the Spelling Error',
      type: 'select',
      instructions: 'A name was spelled letter by letter. One option has a spelling error. Choose the CORRECT version.',
      questions: [
        {
          text: 'The speaker spells: "T-H-O-M-P-S-O-N." Which is correct?',
          options: ['Thomson', 'Thompson', 'Tompson'],
          answer: 'Thompson',
          explanation: 'T-H-O-M-P-S-O-N spells "Thompson." Watch for the silent "P" in the spelling.',
        },
        {
          text: 'The speaker spells: "M-A-C-K-E-N-Z-I-E." Which is correct?',
          options: ['Mackenzie', 'McKenzie', 'Mackenzy'],
          answer: 'Mackenzie',
          explanation: 'M-A-C-K-E-N-Z-I-E = "Mackenzie." Write exactly what is spelled.',
        },
        {
          text: 'The speaker spells: "B-E-A-U-M-O-N-T." Which is correct?',
          options: ['Beaumont', 'Beumont', 'Beamount'],
          answer: 'Beaumont',
          explanation: 'B-E-A-U-M-O-N-T = "Beaumont." The "eau" cluster is common in French-origin names.',
        },
        {
          text: 'The speaker spells: "S-C-H-double-E-L-E." Which is correct?',
          options: ['Scheele', 'Schele', 'Scheelle'],
          answer: 'Scheele',
          explanation: '"Double E" means two E\'s together: S-C-H-E-E-L-E = "Scheele."',
        },
      ],
    },
    {
      title: 'Complete the Address and Phone Number',
      type: 'fill-in',
      instructions: 'Fill in the missing information based on what the speaker says.',
      questions: [
        {
          text: 'The speaker says: "I live at number forty-two Glenfield Road, postcode B-R-seven, three-A-H." Write the full address: ___',
          answer: '42 Glenfield Road, BR7 3AH',
          explanation: 'Forty-two = 42; combine letters and numbers for the postcode.',
        },
        {
          text: 'The speaker says: "You can reach me on oh-seven-nine-double-five-three-two-eight-oh-one." Write the phone number: ___',
          answer: '079 553 2801',
          explanation: '"Double five" = 55. Digits: 0-7-9-5-5-3-2-8-0-1.',
        },
        {
          text: 'The speaker says: "The office is at fifteen-A, Upper Richmond Road, S-W-fifteen, two-T-J." Write the full address: ___',
          answer: '15A Upper Richmond Road, SW15 2TJ',
          explanation: 'Flat 15A; postcodes combine letters and numbers: SW15 2TJ.',
        },
      ],
    },
    {
      title: 'Transcribe Dictated Numbers',
      type: 'writing',
      instructions: 'Imagine you hear the following passages. Write down all the key numerical information as you would on the IELTS answer sheet.',
      questions: [
        {
          text: '"The conference registration fee is two hundred and forty-five dollars. Early bird discount applies until the seventeenth of April. Contact us on oh-eight-hundred, double-six, nine-three-seven-one."',
          example: 'Fee: $245; Deadline: 17 April; Phone: 0800 66 9371',
          tip: 'Break the passage into chunks: price, date, phone number. Write each one as you hear it.',
        },
        {
          text: '"My name is Johansson — that\'s J-O-H-A-N-double-S-O-N. I\'m at flat seven, twenty-three Beechwood Lane, postcode E-C-two, four-P-L. My mobile is oh-seven-four-two-one, triple-eight, nine-five-six."',
          example: 'Name: Johansson; Address: Flat 7, 23 Beechwood Lane, EC2 4PL; Mobile: 07421 888 956',
          tip: 'Write spelling as you hear each letter. For "double" and "triple," immediately write the repeated digit.',
        },
      ],
    },
  ],
  tips: [
    'Before the audio starts, scan the form for the TYPE of information expected (date? price? name?) so you know what to listen for.',
    'Write numbers in digits (42, not forty-two) — it\'s faster and avoids spelling errors.',
    'If you miss a number, leave it blank and keep listening. You can guess during transfer time.',
    'Practice distinguishing teen/tens numbers daily — this is the single most common number error.',
    'For phone numbers, tap a finger for each digit to keep count.',
    'Learn the NATO-style letter confirmations often used: "B for Bravo, P for Papa."',
  ],
  dailyPractice: [
    'Listen to a BBC radio programme and write down every number you hear for 5 minutes.',
    'Have a friend dictate random 7-digit phone numbers; write them without asking to repeat.',
    'Practice spelling 10 random surnames letter by letter, then check your accuracy.',
    'Record yourself saying dates in different formats and transcribe them.',
  ],
  backPath: '/exams/english/#listening',
  backLabel: 'Back to Listening',
  aiContext: 'Understanding Numbers & Spelling: A beginner-level IELTS Listening skill course covering teen vs. tens number confusion, date and time formats, telephone numbers digit-by-digit with double/triple, postcodes and addresses, prices in pounds/dollars, and letter-by-letter spelling of names. Targets Sections 1–2 form/note/table completion questions. Includes 18 practice questions across select, fill-in, and writing exercise types.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...understandingNumbersData,
      title: understandingNumbersData.title + ' (Foundations)',
      overview: { ...understandingNumbersData.overview, duration: '15-20 minutes' },
      sections: understandingNumbersData.sections.slice(0, Math.max(2, Math.ceil(understandingNumbersData.sections.length / 2))),
      exercises: understandingNumbersData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...understandingNumbersData,
      title: understandingNumbersData.title + ' (Advanced)',
      overview: { ...understandingNumbersData.overview, duration: '30-35 minutes' },
      sections: understandingNumbersData.sections,
      exercises: understandingNumbersData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(understandingNumbersData.tips || []),
        'For Band 8+: Train yourself to understand speakers at fast natural speed.',
        'Advanced tip: Practice with varied accents and complex academic content.',
      ],
    };
  }
  return understandingNumbersData;
}
